/**
 * Устройство раздела «Хранилища данных»: группы по виду хранилища (верхний
 * ряд вкладок) и вкладки внутри группы (нижний ряд).
 *
 * Одна таблица STORAGE_GROUPS -- единственное место, где записаны состав
 * групп, порядок вкладок, их адреса, роли и типы источников сканирования
 * группы. И переходы по вкладкам, и обратный разбор адреса выведены из неё,
 * поэтому «вкладка ведёт на адрес, который разбирается в другую вкладку»
 * невозможно по устройству, а не по совпадению двух независимых записей.
 *
 * Модуль не-компонентный (react-refresh/only-export-components) и живёт в
 * каталоге раздела, а не в DataSources/: группа -- это структура раздела
 * хранилищ, а страница источников стала одной из его вкладок. Обратная
 * зависимость (DataStorages -> DataSources/types) уже существует по той же
 * причине -- общие карты типов источников лежат там.
 */

import { type EntityId } from '@/lib/entityId';
import type { DataSource, DataSourceType } from '@/api/dataSources';
import { HIGHLIGHT_PARAM } from '@/pages/DataSources/types';

/** Идентификатор группы хранилищ -- значение вкладки верхнего ряда */
export type StorageGroup = 'sql' | 'nosql' | 'kafka' | 's3';

/**
 * Роль вкладки -- чем вкладка занята, независимо от вида хранилища:
 * 'sources' -- источники сканирования, 'instances' -- инстансы (у Kafka это
 * кластеры), 'inventory' -- сам инвентарь обнаруженного (базы, топики,
 * бакеты). Нужна при переключении группы: оператор, смотревший инвентарь
 * SQL, ожидает увидеть инвентарь Kafka, а не её источники. Роли, которой в
 * новой группе нет (у S3 нет инстансов), соответствия не находится -- тогда
 * переход идёт на вкладку источников.
 */
export type StorageTabRole = 'sources' | 'instances' | 'inventory';

/** Вкладка внутри группы -- значение вкладки нижнего ряда */
export interface StorageTabDef {
	/** Значение вкладки: им же различается содержимое в index.tsx */
	value: string;
	/** Роль вкладки -- см. doc-comment StorageTabRole */
	role: StorageTabRole;
	/** Ключ подписи с пространством имён (storages:…) -- файл не связан с useTranslation */
	labelKey: string;
	/** Адрес вкладки -- тот же, что был до разбивки раздела на группы */
	route: string;
}

export interface StorageGroupDef {
	value: StorageGroup;
	/** Ключ подписи группы с пространством имён (storages:…) */
	labelKey: string;
	/**
	 * Типы источников сканирования DAR, которые показывает вкладка источников
	 * этой группы. Первый -- тот, что подставляется в форму заведения: у SQL и
	 * документных типов несколько, и выбор оператора ограничен этим же списком
	 * (см. availableTypes в DataSourceForm.tsx), чтобы заведённая карточка не
	 * оказалась вне той группы, из которой её заводят.
	 */
	sourceTypes: DataSourceType[];
	/**
	 * Вкладки группы в порядке показа; вкладка источников показывается
	 * последней. Искать её по позиции нельзя: порядок -- решение о виде
	 * раздела и уже менялся, а «вкладка источников» это роль (см.
	 * sourcesTabOfGroup ниже и StorageTabRole выше).
	 */
	tabs: StorageTabDef[];
}

/**
 * Состав раздела. Адреса вкладок инвентаря оставлены прежними: на них
 * ссылаются карта данных (DataMap/RightPanel/DetailsPanel.tsx), дашборд,
 * хлебные крошки страниц деталей и переходы внутри самих вкладок. Новые
 * адреса появились только у вкладок источников.
 */
export const STORAGE_GROUPS: StorageGroupDef[] = [
	{
		value: 'sql',
		labelKey: 'storages:groups.sql',
		sourceTypes: ['postgres', 'mysql', 'mssql'],
		tabs: [
			{
				value: 'sqlInstances',
				role: 'instances',
				labelKey: 'storages:tabs.instances',
				route: '/data-storages/databases/instances',
			},
			{
				value: 'sql',
				role: 'inventory',
				labelKey: 'storages:tabs.databases',
				route: '/data-storages/databases',
			},
			{
				value: 'sqlSources',
				role: 'sources',
				labelKey: 'storages:tabs.sources',
				route: '/data-storages/databases/sources',
			},
		],
	},
	{
		value: 'nosql',
		labelKey: 'storages:groups.nosql',
		sourceTypes: ['mongo', 'cassandra'],
		tabs: [
			{
				value: 'nosqlInstances',
				role: 'instances',
				labelKey: 'storages:tabs.instances',
				route: '/data-storages/nosql-databases/instances',
			},
			{
				value: 'nosql',
				role: 'inventory',
				labelKey: 'storages:tabs.databases',
				route: '/data-storages/nosql-databases',
			},
			{
				value: 'nosqlSources',
				role: 'sources',
				labelKey: 'storages:tabs.sources',
				route: '/data-storages/nosql-databases/sources',
			},
		],
	},
	{
		value: 'kafka',
		labelKey: 'storages:groups.kafka',
		sourceTypes: ['kafka'],
		tabs: [
			{
				value: 'kafkaInstances',
				role: 'instances',
				labelKey: 'storages:tabs.clusters',
				route: '/data-storages/kafka/instances',
			},
			{
				value: 'kafkaTopics',
				role: 'inventory',
				labelKey: 'storages:tabs.topics',
				route: '/data-storages/kafka/topics',
			},
			{
				value: 'kafkaSources',
				role: 'sources',
				labelKey: 'storages:tabs.sources',
				route: '/data-storages/kafka/sources',
			},
		],
	},
	{
		value: 's3',
		labelKey: 'storages:groups.s3',
		sourceTypes: ['s3'],
		tabs: [
			{
				value: 's3',
				role: 'inventory',
				labelKey: 'storages:tabs.buckets',
				route: '/data-storages/s3-buckets',
			},
			{
				value: 's3Sources',
				role: 'sources',
				labelKey: 'storages:tabs.sources',
				route: '/data-storages/s3-buckets/sources',
			},
		],
	},
];

/** Единый список хранилищ (E3.1): индексный адрес раздела и первая крошка вкладок видов */
export const STORAGE_CATALOG_ROUTE = '/data-storages';

/** Вкладка, показываемая на нераспознанном адресе раздела */
export const DEFAULT_STORAGE_ROUTE = '/data-storages/databases';

/** Прежний адрес страницы источников -- остался как перенаправление внутрь раздела */
export const LEGACY_SOURCES_PATH = '/data-sources';

const GROUP_BY_TAB = new Map<string, StorageGroupDef>(
	STORAGE_GROUPS.flatMap((group) => group.tabs.map((tab) => [tab.value, group] as const)),
);

const GROUP_BY_SOURCE_TYPE = new Map<DataSourceType, StorageGroupDef>(
	STORAGE_GROUPS.flatMap((group) => group.sourceTypes.map((type) => [type, group] as const)),
);

const TAB_BY_ROUTE = new Map<string, StorageTabDef>(
	STORAGE_GROUPS.flatMap((group) => group.tabs.map((tab) => [tab.route, tab] as const)),
);

/**
 * Вкладка нераспознанного адреса -- та же, на которую ведёт индексный
 * редирект раздела. Вычислена из таблицы, а не задана литералом: иначе
 * переименование адреса вкладки оставило бы умолчание указывать в пустоту.
 */
const DEFAULT_TAB = TAB_BY_ROUTE.get(DEFAULT_STORAGE_ROUTE) ?? STORAGE_GROUPS[0].tabs[0];
const DEFAULT_GROUP = GROUP_BY_TAB.get(DEFAULT_TAB.value) ?? STORAGE_GROUPS[0];

const GROUP_BY_VALUE = new Map<StorageGroup, StorageGroupDef>(
	STORAGE_GROUPS.map((group) => [group.value, group] as const),
);

/**
 * Описание группы по её идентификатору. Возврат не опциональный: StorageGroup
 * -- закрытое объединение, и каждое его значение заведено в таблице (проверено
 * тестом groups.test.ts), поэтому вызывающим не приходится обрабатывать
 * недостижимое отсутствие.
 */
export function storageGroup(value: StorageGroup): StorageGroupDef {
	return GROUP_BY_VALUE.get(value) ?? DEFAULT_GROUP;
}

/** Группа, которой принадлежит вкладка; для неизвестной вкладки -- группа вкладки по умолчанию */
export function storageGroupOfTab(tab: string): StorageGroupDef {
	return GROUP_BY_TAB.get(tab) ?? DEFAULT_GROUP;
}

/**
 * Адрес вкладки источников группы по умолчанию -- туда ведёт прежний общий
 * адрес источников (LEGACY_SOURCES_PATH, маршрут в AppRoutes.tsx).
 */
export const DEFAULT_SOURCES_ROUTE = sourcesTabOfGroup(DEFAULT_GROUP).route;

/** Группа по типу источника сканирования; undefined -- тип не отнесён ни к одной */
export function storageGroupOfSourceType(type: DataSourceType): StorageGroupDef | undefined {
	return GROUP_BY_SOURCE_TYPE.get(type);
}

/**
 * Показывать ли карточку источника типа type на вкладке источников группы
 * group. Обычно это принадлежность типа группе, но у правила есть вторая
 * половина: тип, не отнесённый ни к одной группе, показывает группа по
 * умолчанию. Сервер может начать отдавать новый вид хранилища раньше, чем о
 * нём узнает фронтенд, и без этой половины такая карточка не появилась бы ни
 * на одной вкладке -- то есть источник сканирует данные, а в интерфейсе его
 * нет вовсе. Полнота перечня для сегодняшних типов держится тестом
 * (groups.test.ts), так что вторая половина -- страховка от расхождения с
 * бэкендом, а не штатный путь.
 */
export function sourceTypeVisibleInGroup(type: DataSourceType, group: StorageGroup): boolean {
	const typeGroup = storageGroupOfSourceType(type);
	return typeGroup ? typeGroup.value === group : group === DEFAULT_GROUP.value;
}

/**
 * Вкладка, на которую ведёт переключение группы: та же роль, что у вкладки, с
 * которой оператор уходит (инвентарь SQL -> инвентарь Kafka), а если такой
 * роли в новой группе нет (у S3 нет инстансов) -- вкладка роли fallback:
 * источников у администратора, инвентаря у тех, кому источники закрыты
 * (canManageSources в index.tsx).
 */
export function tabOfGroupByRole(
	group: StorageGroupDef,
	role: StorageTabRole,
	fallback: StorageTabRole = 'sources',
): StorageTabDef {
	return (
		group.tabs.find((tab) => tab.role === role) ??
		group.tabs.find((tab) => tab.role === fallback) ??
		sourcesTabOfGroup(group)
	);
}

/**
 * Вкладка источников группы -- та, у которой роль 'sources', а не вкладка на
 * определённом месте списка. Порядок вкладок -- решение о виде раздела и
 * может поменяться снова; выбор по позиции при такой перестановке молча увёл
 * бы прежний общий адрес источников, подсветку карточки из формы конфликта и
 * переход при смене группы на соседнюю вкладку. Роль заведена у каждой группы
 * (проверено тестом groups.test.ts), поэтому возврат не опциональный.
 */
export function sourcesTabOfGroup(group: StorageGroupDef): StorageTabDef {
	return group.tabs.find((tab) => tab.role === 'sources') ?? group.tabs[group.tabs.length - 1];
}

/**
 * Разбор адреса в значение вкладки. Сравнение точное (с точностью до
 * замыкающей косой черты), а не по вхождению подстроки: адрес вкладки
 * источников группы SQL (/data-storages/databases/sources) содержит в себе
 * адрес вкладки баз (/data-storages/databases), и проверка на вхождение
 * относила бы его к вкладке баз -- порядок проверок стал бы несущим.
 * Страницы деталей сюда не попадают: их адреса обслуживают собственные
 * маршруты (AppRoutes.tsx).
 */
export function storageTabFromPath(pathname: string): string {
	const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
	return (TAB_BY_ROUTE.get(normalized) ?? DEFAULT_TAB).value;
}

/**
 * Адрес вкладки источников, на которой видна карточка source, с параметром
 * подсветки. Тип карточки известен не всегда: каталог источников в форме
 * (DataSourceForm.tsx) может не содержать карточку, о конфликте с которой
 * сообщил сервер -- её могли завести рядом уже после загрузки списка. В этом
 * случае адресом становится прежний общий путь: он ведёт в группу по
 * умолчанию, а вкладка источников сама доводит оператора до нужной группы,
 * как только список карточек получен (см. эффект перенаправления в
 * DataSources/index.tsx).
 */
export function sourceHighlightPath(sourceId: EntityId, source?: DataSource): string {
	const group = source ? storageGroupOfSourceType(source.type) : undefined;
	const base = group ? sourcesTabOfGroup(group).route : LEGACY_SOURCES_PATH;
	return `${base}?${HIGHLIGHT_PARAM}=${sourceId}`;
}
